/**
 * Connection awareness utilities for multi-leg trip handling.
 *
 * Classifies flight legs (departure, connection, arrival) and determines
 * which preference questions to ask for each leg type.
 */

/**
 * Classify an array of flights into departure, connection, and arrival legs.
 * Flights are expected to be sorted by departure time.
 *
 * - Single flight → labeled 'departure'
 * - Two flights → first is 'departure', last is 'arrival'
 * - Three or more → first is 'departure', last is 'arrival', middle are 'connection'
 *
 * @param {Array} flights - Array of flight objects sorted by departure time
 * @returns {Array<{ flight: object, legType: 'departure' | 'connection' | 'arrival' }>}
 */
export function classifyFlightLegs(flights) {
  if (!flights || flights.length === 0) return [];
  if (flights.length === 1) {
    return [{ flight: flights[0], legType: 'departure' }];
  }
  return flights.map((flight, index) => ({
    flight,
    legType: index === 0 ? 'departure'
           : index === flights.length - 1 ? 'arrival'
           : 'connection',
  }));
}

/**
 * Get the preference question keys to ask for a given leg type.
 *
 * - Departure: transportMode, wantsLoungeInfo
 * - Arrival: hasCheckedBags, preferredGroundTransport
 * - Connection: wantsLoungeInfo only if layover exceeds 120 minutes
 *
 * @param {string} legType - One of 'departure', 'arrival', 'connection'
 * @param {number} [layoverMinutes=0] - Layover duration in minutes (relevant for connections)
 * @returns {string[]} Array of preference question keys
 */
export function getQuestionsForLeg(legType, layoverMinutes = 0) {
  switch (legType) {
    case 'departure':
      return ['transportMode', 'wantsLoungeInfo'];
    case 'arrival':
      return ['hasCheckedBags', 'preferredGroundTransport'];
    case 'connection':
      return layoverMinutes > 120 ? ['wantsLoungeInfo'] : [];
    default:
      return [];
  }
}
